import type { JSX, ReactNode } from "react";

import { useNavCollapsed } from "@/shared/hooks/useNavCollapsed";
import { Toaster } from "@/shared/ui/toaster";
import { TooltipProvider } from "@/shared/ui/tooltip";

import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

export function AppShell({
  children,
  minimal = false,
}: {
  children: ReactNode;
  // Skips the TopBar/Sidebar chrome while keeping TooltipProvider/Toaster
  // mounted — used for the locked-down /change-password screen so that
  // switching in/out of chrome doesn't tear down and remount the tooltip
  // context (Radix throws if a Tooltip renders while its provider is
  // mid-unmount).
  minimal?: boolean;
}): JSX.Element {
  const [collapsed, toggle] = useNavCollapsed();

  if (minimal) {
    return (
      <TooltipProvider delayDuration={200}>
        <Toaster />
        {children}
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Toaster />
      <div
        className="min-h-screen"
        style={{ background: "var(--grad-canvas)" }}
        data-testid="app-shell"
      >
        <TopBar collapsed={collapsed} onToggle={toggle} />
        <Sidebar collapsed={collapsed} />
        <main
          className={[
            "min-h-[calc(100vh-60px)] p-7 transition-[margin] duration-200",
            collapsed ? "ml-[64px]" : "ml-[236px]",
          ].join(" ")}
          data-testid="app-shell-main"
        >
          {children}
        </main>
      </div>
    </TooltipProvider>
  );
}
